import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import type { MCQItem } from '../../services/mockQuizService';
import type { QuizProgress } from '../QuizBlock';

interface MCQPreviewProps {
  items: MCQItem[];
  initialAnswers?: Record<number, { selected: number; correct: boolean }>;
  onAnswersChange?: (answers: Record<number, { selected: number; correct: boolean }>) => void;
  onProgressChange?: (progress: QuizProgress) => void;
}

export const MCQPreview = ({ items, initialAnswers, onAnswersChange, onProgressChange }: MCQPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, { selected: number; correct: boolean }>>(initialAnswers || {});

  const currentItem = items[currentIndex];
  const currentAnswer = answers[currentIndex];
  const showResult = currentAnswer !== undefined;
  const isCorrect = currentAnswer?.correct;

  // Calculate and report progress
  const updateProgress = useCallback(() => {
    const completed = Object.keys(answers).length;
    const correct = Object.values(answers).filter(a => a.correct).length;
    onProgressChange?.({ total: items.length, completed, correct });
  }, [answers, items.length, onProgressChange]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleSubmit = (selectedIndex: number) => {
    const correct = selectedIndex === currentItem.answer;
    const newAnswers = {
      ...answers,
      [currentIndex]: { selected: selectedIndex, correct }
    };
    setAnswers(newAnswers);
    onAnswersChange?.(newAnswers);
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (!currentItem) return null;

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">
          Question {currentIndex + 1} of {items.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === items.length - 1}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Question */}
      <p className="text-sm text-slate-700 mb-4">{currentItem.question}</p>

      {/* Options */}
      <div className="space-y-2 mb-4">
        {currentItem.options.map((option, index) => {
          const isSelected = currentAnswer?.selected === index;
          const isCorrectOption = index === currentItem.answer;

          let bgColor = 'bg-white border-slate-200 hover:bg-slate-50 cursor-pointer';
          if (showResult) {
            if (isCorrectOption) {
              bgColor = 'bg-emerald-50 border-emerald-300 text-emerald-800';
            } else if (isSelected && !isCorrect) {
              bgColor = 'bg-red-50 border-red-300 text-red-800';
            }
          } else if (isSelected) {
            bgColor = 'bg-slate-100 border-slate-400';
          }

          return (
            <button
              key={index}
              onClick={() => !showResult && handleSubmit(index)}
              disabled={showResult}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors flex items-center gap-2 ${bgColor} ${showResult ? 'cursor-default' : ''}`}
            >
              <span className="flex-1">{option}</span>
              {showResult && isCorrectOption && <Check className="w-4 h-4 text-emerald-600" />}
              {showResult && isSelected && !isCorrect && <X className="w-4 h-4 text-red-600" />}
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {showResult && currentItem.explanation && (
        <div className={`p-3 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <p className="font-medium mb-1">{isCorrect ? '✓ Correct!' : '✗ Incorrect'}</p>
          <p className="text-slate-600">{currentItem.explanation}</p>
        </div>
      )}

      {/* Progress dots */}
      <div className="flex justify-center gap-1 mt-4">
        {items.map((_, idx) => {
          const answered = answers[idx] !== undefined;
          const wasCorrect = answers[idx]?.correct;
          return (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex
                  ? 'bg-slate-600 ring-2 ring-slate-300'
                  : answered
                  ? wasCorrect ? 'bg-emerald-400' : 'bg-red-400'
                  : 'bg-slate-200 hover:bg-slate-300'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
